import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { getImageUrl } from '../../utils/imageUtils';

const ListingsManagement = () => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadListings();
  }, [filterStatus]);

  const loadListings = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/listings?status=${filterStatus}`);
      setListings(response.data.listings || []);
    } catch (error) {
      toast.error('Error loading listings');
    } finally {
      setLoading(false);
    }
  };

  const deleteListing = async (listingId) => {
    if (!window.confirm('Are you sure you want to delete this listing?')) {
      return;
    }

    try {
      await api.delete(`/admin/listings/${listingId}`);
      toast.success('Listing deleted successfully');
      loadListings();
    } catch (error) {
      toast.error('Error deleting listing');
    }
  };

  const getStatusBadge = (listing) => {
    if (new Date(listing.expiresAt) <= new Date()) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold text-white shadow-md" style={{ backgroundColor: '#dc2626' }}>
          <i className="fas fa-clock mr-1"></i>
          Expired
        </span>
      );
    } else if (listing.isActive) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold text-white shadow-md" style={{ backgroundColor: '#948979' }}>
          <i className="fas fa-check-circle mr-1"></i>
          Active
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold text-white shadow-md" style={{ backgroundColor: '#393E46' }}>
          <i className="fas fa-pause-circle mr-1"></i>
          Inactive
        </span>
      );
    }
  };

  const filteredListings = listings.filter(listing => 
    listing.itemName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    listing.owner?.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-t-4 rounded-full animate-spin mx-auto mb-4" 
               style={{ borderColor: '#DFD0B8', borderTopColor: '#948979' }}></div>
          <p className="font-medium" style={{ color: '#393E46' }}>Loading listings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="space-y-6 lg:space-y-8">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6 lg:mb-8">
        <div>
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-2xl flex items-center justify-center shadow-lg" style={{ backgroundColor: '#222831' }}>
              <i className="fas fa-list text-white text-lg lg:text-xl"></i>
            </div>
            <h2 className="text-2xl lg:text-3xl font-bold" style={{ color: '#222831' }}>
              Listings Management
            </h2>
          </div>
          <p className="ml-13 lg:ml-15 text-sm lg:text-base" style={{ color: '#393E46' }}>Manage all platform listings and their status</p>
        </div>
        <div className="mt-4 lg:mt-0">
          <div className="px-3 py-2 lg:px-4 lg:py-2 rounded-2xl shadow-lg text-sm lg:text-base" style={{ backgroundColor: '#948979' }}>
            <span className="font-bold text-white">{filteredListings.length}</span>
            <span className="ml-1 text-white">Listings</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl lg:rounded-3xl shadow-xl p-6 lg:p-8 border-2" style={{ borderColor: '#DFD0B8' }}>
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#948979' }}>
            <i className="fas fa-filter text-white text-sm"></i>
          </div>
          <h3 className="text-xl font-bold" style={{ color: '#222831' }}>Filter Listings</h3>
        </div>
        <div className="flex flex-col md:flex-row gap-6">
          <div className="flex-1">
            <label className="block text-sm font-bold mb-3" style={{ color: '#393E46' }}>Search Listings</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <i className="fas fa-search" style={{ color: '#948979' }}></i>
              </div>
              <input
                type="text"
                placeholder="Search by item name or owner..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-4 border-2 rounded-2xl focus:ring-2 focus:border-transparent transition-all duration-200 font-medium"
                style={{ 
                  borderColor: '#DFD0B8', 
                  backgroundColor: '#DFD0B8',
                  color: '#222831'
                }}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-bold mb-3" style={{ color: '#393E46' }}>Status Filter</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-6 py-4 border-2 rounded-2xl focus:ring-2 focus:border-transparent transition-all duration-200 font-medium"
              style={{ 
                borderColor: '#DFD0B8', 
                backgroundColor: '#DFD0B8',
                color: '#222831'
              }}
            >
              <option value="all">All Listings</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
            </select>
          </div>
        </div>
      </div>

      {/* Listings Grid */}
      <div className="space-y-4 lg:space-y-6">
        {filteredListings.length === 0 ? (
          <div className="bg-white rounded-2xl lg:rounded-3xl shadow-xl p-12 lg:p-16 text-center border-2" style={{ borderColor: '#DFD0B8' }}>
            <div className="w-24 h-24 rounded-3xl flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#DFD0B8' }}>
              <i className="fas fa-list text-4xl" style={{ color: '#948979' }}></i>
            </div>
            <h3 className="text-xl font-bold mb-2" style={{ color: '#222831' }}>No listings found</h3>
            <p style={{ color: '#393E46' }}>No listings match the current filter criteria</p>
          </div>
        ) : (
          filteredListings.map(listing => (
            <div key={listing._id} className="bg-white rounded-2xl lg:rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 lg:p-8 border-2 overflow-hidden" style={{ borderColor: '#DFD0B8' }}>
              <div className="flex flex-col sm:flex-row sm:items-start space-y-4 sm:space-y-0 sm:space-x-4 lg:space-x-6">
                {/* Listing Image */}
                <div className="flex-shrink-0 self-center sm:self-start">
                  <img 
                    src={getImageUrl(listing.images[0])} 
                    alt={listing.itemName}
                    className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-xl lg:rounded-2xl shadow-lg"
                  />
                </div>

                {/* Listing Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between space-y-4 lg:space-y-0">
                    <div className="space-y-3 flex-1 min-w-0">
                      <div className="text-center sm:text-left">
                        <h3 className="text-lg lg:text-xl font-bold truncate" style={{ color: '#222831' }}>{listing.itemName}</h3>
                        <p className="font-medium text-sm lg:text-base" style={{ color: '#393E46' }}>
                          ₹{listing.price}/day • {listing.size} • {listing.location}
                        </p>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex items-center justify-center sm:justify-start space-x-2">
                          <i className="fas fa-user" style={{ color: '#948979' }}></i>
                          <span className="font-medium text-sm lg:text-base truncate" style={{ color: '#222831' }}>
                            {listing.owner?.name} ({listing.owner?.email})
                          </span>
                        </div>
                        <div className="flex items-center justify-center sm:justify-start space-x-2">
                          <i className="fas fa-phone" style={{ color: '#948979' }}></i>
                          <span className="text-sm lg:text-base" style={{ color: '#393E46' }}>{listing.owner?.phone}</span>
                        </div>
                      </div>

                      <div className="flex flex-wrap justify-center sm:justify-start gap-2">
                        {getStatusBadge(listing)}
                        <span className="px-2 py-1 rounded-full text-xs font-bold text-white shadow-md" style={{ backgroundColor: '#393E46' }}>
                          {listing.category || 'Handycraft'}
                        </span>
                        <span className="px-2 py-1 rounded-full text-xs font-bold text-white shadow-md" style={{ backgroundColor: '#222831' }}>
                          {listing.clothingType}
                        </span>
                      </div>

                      <p className="text-xs lg:text-sm leading-relaxed text-center sm:text-left" style={{ color: '#393E46' }}>
                        {listing.description?.substring(0, 150)}...
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex justify-center lg:justify-end">
                      <button
                        onClick={() => deleteListing(listing._id)}
                        className="px-3 py-2 lg:px-4 lg:py-2 rounded-xl lg:rounded-2xl text-white font-bold hover:shadow-lg transition-all duration-200 bg-red-500 hover:bg-red-600"
                      >
                        <i className="fas fa-trash mr-1 lg:mr-2"></i>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      </div>
    </div>
  );
  
};

export default ListingsManagement;